import { I18n } from 'react-redux-i18n';
import React from 'react';
import styled from 'styled-components';
import {
  SortableHeaderButton,
  type TableSortState
} from '../../components/SortableTable';

export type GroupBy = 'brand' | 'wine';

export type PosStatusChip = 'all' | 'active' | 'lost' | 'towin' | 'other';

export function countRefs(value?: string | null): number {
  if (!value) return 0;
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean).length;
}

export function formatRawWineFormat(formatGroupId?: number | null): string {
  if (formatGroupId === 1) return I18n.t('v2GlobalView.rawData.glass');
  if (formatGroupId === 2) return I18n.t('v2GlobalView.rawData.bottle');
  if (formatGroupId === 3) return I18n.t('v2GlobalView.rawData.largeFormat');
  if (formatGroupId === 4) return I18n.t('v2GlobalView.rawData.half');
  return '—';
}

// Rows shown at once (proto-2's "gvPosRowLimit"): 100 by default, up to 1000.
export const GV_ROW_LIMIT_OPTIONS = [100, 500, 1000] as const;

export const LoadingWrap = styled.div`
  padding: 48px 0;
  text-align: center;
  color: var(--ink-faint);
  font-size: 13px;
`;

export const TableWrap = styled.div`
  background: #fff;
  border-radius: 14px;
  border: 1px solid var(--line-soft, #e8e5df);
  overflow-x: auto;
  overflow-y: hidden;
`;

export const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  padding: 18px 20px 16px;
  border-bottom: 1px solid var(--line-soft, #e8e5df);
`;

export const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin: 0 0 6px;
`;

export const CardTitle = styled.h2`
  font-family: var(--font-sans);
  font-size: 17px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--ink);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 2px;
`;

export const CardDesc = styled.p`
  font-size: 13px;
  color: var(--ink-muted);
  line-height: 1.5;
  max-width: 640px;
  margin: 0;
`;

export const CardHeadActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-left: auto;
`;

export const GroupByWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border: 1px solid var(--line-soft, #e8e5df);
  border-radius: 999px;
  background: var(--paper, #f9f7f4);
`;

// Floating "Show 100/500/1000" row-limit control (proto-2's
// `#gvPosRowLimit` / `#gvRawRowLimit`), placed under the table.
export const RowLimitBar = styled(GroupByWrap)`
  margin: 10px 0;
`;

export const RowLimitFooter = styled.div`
  display: flex;
  justify-content: center;
  padding: 6px 16px;
  border-top: 1px solid var(--line-soft, #e8e5df);
`;

export const GroupByBtn = styled.button<{ $active: boolean }>`
  border: 0;
  border-radius: 999px;
  padding: 5px 12px;
  background: ${({ $active }) => ($active ? 'var(--ink)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--white)' : 'var(--ink)')};
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

export const ColDisplayTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 13px;
  border-radius: 7px;
  border: 1px solid var(--line);
  background: var(--white);
  color: var(--ink);
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    border-color: var(--ink);
  }
`;

export const ColDisplayBadge = styled.span`
  background: var(--terracotta-soft, #fef3ee);
  color: var(--terracotta, #c84b1f);
  border-radius: 999px;
  padding: 1px 7px;
  font-size: 11px;
  font-weight: 700;
`;

export const TrackingTabCount = styled.span`
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 100px;
  background: var(--white);
  color: var(--ink-muted);
  font-weight: 600;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

export const Th = styled.th<{
  $align?: string;
  $sort?: boolean;
  $wrap?: boolean;
}>`
  padding: 14px 16px;
  text-align: ${({ $align }) => $align || 'left'};
  font-weight: 600;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ink-faint);
  border-bottom: 1px solid var(--line-soft, #e8e5df);
  white-space: ${({ $wrap }) => ($wrap ? 'normal' : 'nowrap')};
  ${({ $wrap }) => $wrap && 'vertical-align: bottom; line-height: 1.3;'}
  ${({ $sort }) =>
    $sort &&
    `
    &::after {
      content: ' ↓';
      opacity: 0.5;
    }
  `}
`;

export const SortableTh: React.FC<{
  label: React.ReactNode;
  sortKey: string;
  sort: TableSortState;
  onSort: (key: string) => void;
  align?: 'left' | 'right';
}> = ({ label, sortKey, sort, onSort, align = 'left' }) => (
  <Th
    $align={align}
    aria-sort={sort?.key === sortKey ? sort.direction : 'none'}
  >
    <SortableHeaderButton
      label={label}
      active={sort?.key === sortKey}
      direction={sort?.direction}
      align={align}
      onClick={() => onSort(sortKey)}
    />
  </Th>
);

export const Tr = styled.tr<{ $selected?: boolean }>`
  cursor: pointer;
  background: ${({ $selected }) =>
    $selected ? 'rgba(200, 75, 31, 0.06)' : 'transparent'};
  &:not(:last-child) td {
    border-bottom: 1px solid var(--line-soft, #e8e5df);
  }
  &:hover {
    background: ${({ $selected }) =>
      $selected ? 'rgba(200, 75, 31, 0.06)' : 'var(--paper, #f9f7f4)'};
  }
`;

export const Td = styled.td`
  padding: 14px 16px;
  vertical-align: middle;
`;

export const TdNum = styled.td<{ $positive?: boolean; $negative?: boolean }>`
  padding: 14px 16px;
  text-align: right;
  vertical-align: middle;
  font-variant-numeric: tabular-nums;
  color: ${({ $positive, $negative }) =>
    $positive
      ? 'var(--success, #247a52)'
      : $negative
        ? 'var(--danger, #b43b32)'
        : 'inherit'};
`;

export const NoData = styled.span`
  color: var(--ink-faint);
`;

export const PosNameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const PosSubtitle = styled.span`
  font-size: 12px;
  color: var(--ink-faint);
`;

export const EmptyChartState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  border-radius: 10px;
  background: var(--paper);
  color: var(--ink-faint);
  font-size: 12.5px;
  text-align: center;
`;

export interface PriceChartPoint {
  vintage: string;
  value: number | null;
  compare: number | null;
  minPrice: number | null;
  medianPrice: number | null;
  maxPrice: number | null;
  facing: number | null;
  posCount: number | null;
}

export interface VintageYearRow {
  consumptionRate: number | null;
  vintage: string;
  minPrice: number | null;
  medianPrice: number | null;
  maxPrice: number | null;
  nd: number;
  brandRefs: number;
  competitorRefs: number;
  posCount: number;
}

const ExportBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: 7px;
  background: var(--white);
  color: var(--ink);
  cursor: pointer;
  transition: all 0.15s;

  svg {
    width: 15px;
    height: 15px;
  }

  &:hover:not(:disabled) {
    border-color: var(--ink);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ExportButton: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  label?: string;
}> = ({ onClick, disabled, label = I18n.t('v2GlobalView.export.xlsx') }) => (
  <ExportBtn
    type="button"
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  </ExportBtn>
);

export const ToastBox = styled.div<{ $variant: 'info' | 'error' }>`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 2000;
  max-width: 360px;
  padding: 12px 16px;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  background: ${({ $variant }) =>
    $variant === 'error' ? '#b3261e' : '#273544'};
`;
